package com.eladfinish.bible.contest.features.deeplink

import com.eladfinish.bible.contest.navigation.BrowseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.CompleteChapterScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.CompleteVerseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.DebugFlagsScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.LexicographicalSortScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.MikudScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.MiscScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.MonopolyScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.MultipleChoiceQuestionsScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.NamesScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.NavigationDestination
import com.eladfinish.bible.contest.navigation.OpenQuestionsScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.PairMatchingScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.PyramidScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.RandomVerseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.RootNavigationDestination
import com.eladfinish.bible.contest.navigation.SearchScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.SettingsRootNavigationDestination
import com.eladfinish.bible.contest.navigation.SettingsScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.ShonotScreenNavigationDestination
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.ShonotEntry
import net.thauvin.erik.urlencoder.UrlEncoderUtil

/**
 * The screen names ("slugs") of the app's URLs, one mapping for every platform: the web app's URL
 * fragment (`/app/#names`, `#search?q=…`, RootContentJs.kt) and the mobile app's links to an inner
 * screen (`https://biblecontest-f3b7c.web.app/open/names`, [DeepLinks]), whose landing page sends a
 * browser without the app to the same screen of the web app.
 *
 * A slug is the destination's class name without "NavigationDestination"/"Screen", in kebab-case
 * ([slugOf]). The slugs below are spelled out rather than read from the class at runtime, since R8
 * renames classes in the Android release build; ScreenSlugsTest checks that they match [slugOf].
 */
object ScreenSlugs {

    const val ROOT = "root"
    const val SEARCH = "search"
    const val SEARCH_QUERY_PARAM = "q"
    const val SHONOT = "shonot"
    const val SHONOT_ENTRY_PARAM = "entry"

    // The slug alone can't carry constructor arguments, so a destination is only deep-linkable this
    // way if it can be constructed with all-default arguments — landing on the screen itself (e.g.
    // Search with an empty query, Browse at its own picker) rather than the exact state the user
    // had scrolled/typed to. That's still far better than bouncing to the root screen on refresh.
    val destinationsBySlug: Map<String, NavigationDestination> = linkedMapOf(
        ROOT to RootNavigationDestination,
        "names" to NamesScreenNavigationDestination,
        "settings-root" to SettingsRootNavigationDestination,
        "settings" to SettingsScreenNavigationDestination,
        "debug-flags" to DebugFlagsScreenNavigationDestination,
        "pyramid" to PyramidScreenNavigationDestination,
        "misc" to MiscScreenNavigationDestination,
        "monopoly" to MonopolyScreenNavigationDestination,
        "random-verse" to RandomVerseScreenNavigationDestination,
        "complete-verse" to CompleteVerseScreenNavigationDestination,
        "complete-chapter" to CompleteChapterScreenNavigationDestination,
        "pair-matching" to PairMatchingScreenNavigationDestination,
        "mikud" to MikudScreenNavigationDestination,
        SEARCH to SearchScreenNavigationDestination(),
        "browse" to BrowseScreenNavigationDestination(),
        "multiple-choice-questions" to MultipleChoiceQuestionsScreenNavigationDestination(),
        "open-questions" to OpenQuestionsScreenNavigationDestination(),
        "lexicographical-sort" to LexicographicalSortScreenNavigationDestination(),
        SHONOT to ShonotScreenNavigationDestination(),
    )

    private val camelCaseBoundary = Regex("([a-z0-9])([A-Z])")

    /**
     * Turns a destination class name (e.g. "RandomVerseScreenNavigationDestination", or its fully
     * qualified route) into a readable URL slug ("random-verse"): drops the
     * "NavigationDestination"/"Screen" suffixes every destination name shares, then converts the
     * remaining PascalCase to kebab-case.
     */
    fun slugOf(destinationName: String): String =
        destinationName.substringAfterLast('.')
            .removeSuffix("NavigationDestination")
            .removeSuffix("Screen")
            .ifEmpty { ROOT }
            .replace(camelCaseBoundary) { "${it.groupValues[1]}-${it.groupValues[2]}" }
            .lowercase()

    /**
     * Encodes the sliver of a screen's state worth surviving a refresh or a shared link — e.g. the
     * text typed into Search — as a URL query string ("key=value&..."), or `null` if this screen has
     * nothing worth preserving beyond its slug (the common case: [destinationsBySlug] already covers
     * it with default arguments).
     *
     * To add one for a new screen: add a branch here and a matching one in [destinationFor] that
     * parses it back. Keep both in sync — this is the encode half, that's the decode half.
     */
    fun queryFor(destination: NavigationDestination): String? = when (destination) {
        is SearchScreenNavigationDestination -> destination.text
            .takeIf { it.isNotEmpty() }
            ?.let { "$SEARCH_QUERY_PARAM=" + UrlEncoderUtil.encode(it) }

        is ShonotScreenNavigationDestination -> "$SHONOT_ENTRY_PARAM=" + UrlEncoderUtil.encode(destination.entry)

        else -> null
    }

    /** The decode half of [queryFor] — see its doc. Falls back to [destinationsBySlug]. */
    fun destinationFor(slug: String, query: String): NavigationDestination? = when (slug) {
        SEARCH -> SearchScreenNavigationDestination(text = queryParam(query, SEARCH_QUERY_PARAM).orEmpty())
        // An unknown entry (a link from a later version, a typo) opens on the first one.
        SHONOT -> ShonotScreenNavigationDestination(
            entry = (ShonotEntry.fromSlug(queryParam(query, SHONOT_ENTRY_PARAM)) ?: ShonotEntry.ROSH_CHODESH).slug,
        )

        else -> destinationsBySlug[slug]
    }

    /** The decoded value of [name] in [query] ("a=1&b=2", without the '?'), or `null` without one. */
    fun queryParam(query: String, name: String): String? =
        query.split('&')
            .firstOrNull { it.startsWith("$name=") }
            ?.substringAfter('=')
            ?.let { UrlEncoderUtil.decode(it, plusToSpace = true) }
}
